/**
 * Special Pricing Store - Zustand
 *
 * Customer-specific pricing overlay (Special Pricing module). Holds the current
 * store/client's active price-list prices keyed by ProductSkuVariantID. This map
 * is fed as one extra "lowest-wins" candidate into the pricing math
 * (ProductQuantityCalculator + cartServices) so the storefront shows the resolved
 * price with the regular price crossed out — exactly like the admin order modal.
 *
 * The map is fetched once per client from the shop-owned, self-scoped endpoint
 * (`/storefront/client-prices`; X-Client-Id is auto-attached by the axios
 * interceptor) and refreshed when the store is switched.
 */

import { create } from 'zustand';
import { apiService, tokenManager } from '../services/api.js';

const useSpecialPricingStore = create((set, get) => ({
  clientId: null,
  priceMap: {}, // { [productSkuVariantID]: { unitPrice, suppressUnitFee } }
  loadedAt: null,
  isLoading: false,

  /**
   * Fetch the map for the current store if it isn't already loaded for that client.
   * Safe to call repeatedly (no-op once loaded for the active client).
   */
  ensureLoaded: async () => {
    const cid = tokenManager.getCurrentStore()?.id || null;
    if (!cid) {
      // No client context (logged out / employee preview): nothing to overlay.
      if (get().clientId !== null || get().loadedAt) {
        set({ clientId: null, priceMap: {}, loadedAt: null });
      }
      return;
    }
    if (get().clientId === cid && get().loadedAt) return; // already loaded for this client
    await get()._fetch(cid);
  },

  /** Force a reload for the current store (used on store switch). */
  reload: async () => {
    const cid = tokenManager.getCurrentStore()?.id || null;
    if (!cid) {
      set({ clientId: null, priceMap: {}, loadedAt: null });
      return;
    }
    await get()._fetch(cid);
  },

  _fetch: async (cid) => {
    set({ isLoading: true });
    try {
      const res = await apiService.get(`/storefront/client-prices?clientId=${cid}`);
      const map = {};
      (res?.items || []).forEach((i) => {
        // JSON camelCases the DTO's ProductSkuVariantID -> productSkuVariantID.
        // suppressUnitFee = the winning list waives the broken-case fee for this variant.
        if (i.productSkuVariantID != null) {
          map[i.productSkuVariantID] = { unitPrice: i.unitPrice, suppressUnitFee: !!i.suppressUnitFee };
        }
      });
      set({ clientId: cid, priceMap: map, loadedAt: Date.now(), isLoading: false });
    } catch (err) {
      console.error('Error loading special pricing:', err);
      // Fail open: no overlay (regular pricing) rather than blocking the storefront.
      set({ clientId: cid, priceMap: {}, loadedAt: Date.now(), isLoading: false });
    }
  },

  clear: () => set({ clientId: null, priceMap: {}, loadedAt: null, isLoading: false }),
}));

export default useSpecialPricingStore;

/**
 * Non-React accessor for the pricing math (ProductQuantityCalculator, cartServices).
 * Returns the customer's special unit price for a variant, or null if none applies.
 */
export const getSpecialUnitPrice = (variantId) => {
  if (variantId == null) return null;
  const entry = useSpecialPricingStore.getState().priceMap?.[variantId];
  return entry?.unitPrice != null ? Number(entry.unitPrice) : null;
};

/**
 * Whether the variant's special-pricing list waives the single-unit (broken-case) fee.
 * Only meaningful when the special price actually wins — callers gate on that.
 */
export const getSpecialUnitFeeSuppressed = (variantId) => {
  if (variantId == null) return false;
  return !!useSpecialPricingStore.getState().priceMap?.[variantId]?.suppressUnitFee;
};
